import {
  Button,
  capitalize,
  CircularProgress,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { classes } from "common/theme/theme";
import { Field, useFormikContext } from "formik";
import { GroundPrepConfirmDetailsSection } from "./ground-prep/ConfirmDetailsSection";
import { InSeasonConfirmDetailsSection } from "./in-season/ConfirmDetailsSection";
import {
  jobFieldIds,
  makeJobName,
  neighboringStep,
  NewJobsFormState,
} from "./FormState";
import { JobType } from "protos/atk/job_type";
import { LaserWeedingConfirmDetailsSection } from "./laser-weeding/ConfirmDetailsSection";
import { NewJobsWizardFormProps } from "./NewJobsWizard";
import { TextField } from "formik-mui";
import { TransportConfirmDetailsSection } from "./transport/ConfirmDetailsSection";
import { typedPath } from "typed-path";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import React from "react";

const S = typedPath<NewJobsFormState>();

type ConfirmFormProps = Pick<
  NewJobsWizardFormProps,
  "farmMapData" | "farmIdByFieldId"
>;

export const ConfirmForm: React.FC<ConfirmFormProps> = ({
  farmMapData,
  farmIdByFieldId,
}) => {
  const { values, setFieldValue, setFieldTouched, isSubmitting, submitForm } =
    useFormikContext<NewJobsFormState>();
  const { t } = useTranslation();
  const prevStep = neighboringStep(values, "prev");
  const fieldIds = jobFieldIds(values, farmMapData, farmIdByFieldId);

  let details: JSX.Element | undefined;

  switch (values.jobType) {
    case JobType.GROUND_PREP: {
      details = <GroundPrepConfirmDetailsSection />;
      break;
    }
    case JobType.LASER_WEED: {
      details = <LaserWeedingConfirmDetailsSection />;
      break;
    }
    case JobType.TRANSPORT: {
      details = <TransportConfirmDetailsSection />;
      break;
    }
    case JobType.IN_SEASON: {
      details = (
        <InSeasonConfirmDetailsSection
          farmMapData={farmMapData}
          farmIdByFieldId={farmIdByFieldId}
        />
      );
      break;
    }
  }

  return (
    <>
      <Paper
        variant="outlined"
        className="bg-inherit p-4 flex flex-col gap-4 rounded-lg"
      >
        <Typography variant="h5" className="">
          {capitalize(t("views.autotractor.jobCreation.step4Confirm.title"))}
        </Typography>
        <Typography color="textSecondary">
          {t("views.autotractor.jobCreation.step4Confirm.description")}
        </Typography>
        <hr className="w-full" />
        <FormControl className="w-1/2">
          <FormLabel id="job-name-label">
            {t("views.autotractor.jobCreation.step4Confirm.jobNameLabel")}
          </FormLabel>
          <Field
            component={TextField}
            name={S.jobName.$path}
            aria-labelledby="job-name-label"
          />
        </FormControl>
        <Paper className="p-4">
          <Typography variant="h6">
            {t("views.autotractor.jobCreation.step4Confirm.jobDetailsLabel")}
          </Typography>
          {details}
        </Paper>
        {values.jobName && (
          <>
            <hr className="w-full" />
            <Typography variant="h5">
              {t(
                "views.autotractor.jobCreation.step4Confirm.jobsToCreateLabel",
                { count: fieldIds.length }
              )}
            </Typography>
            <List className="max-h-[50vh]">
              {fieldIds.map((fieldId) => {
                const farmId = farmIdByFieldId[fieldId];
                const farm = farmMapData[farmId ?? ""];
                const field = farm?.zonesById[fieldId];

                if (farm && field) {
                  return (
                    <ListItem
                      key={farmId}
                      className={classes(
                        "border-x border-t border-b-0 border-solid",
                        "first:rounded-t-lg last:rounded-b-lg last:border-b"
                      )}
                    >
                      <ListItemText primary={makeJobName(values, field.name)} />
                    </ListItem>
                  );
                }
              })}
            </List>
          </>
        )}
      </Paper>
      <div className="w-full flex flex-row gap-4 items-center">
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => {
            setFieldValue(S.activeStep.$path, prevStep);
            setFieldTouched(S.activeStep.$path, true);
          }}
          disabled={isSubmitting}
        >
          {t("utils.actions.backShort")}
        </Button>
        <div className="flex-1" />
        <Typography variant="body2">
          {t("views.autotractor.jobCreation.step4Confirm.jobCreateSlowNote")}
        </Typography>
        <Button
          variant="contained"
          color="success"
          size="large"
          type="submit"
          onClick={submitForm}
          startIcon={
            isSubmitting ? (
              <CircularProgress variant="indeterminate" size="2rem" />
            ) : (
              <CheckIcon />
            )
          }
          disabled={!values.jobName || isSubmitting}
        >
          {t("views.autotractor.jobCreation.step4Confirm.createJobs")}
        </Button>
      </div>
    </>
  );
};
